import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  IL_COLORS,
  formatCurrency,
  formatSignedPercent,
} from "../../../../constants/investmentLeague";

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const RecipientDetailDrawer = ({ unit, onClose }) => {
  const theme = useTheme();
  const t = theme.custom;
  const [showAllInvestors, setShowAllInvestors] = useState(false);

  if (!unit) return null;

  // Format 3-decimal variance if available
  const formatVariance = (roi) => {
    if (roi == null || roi === "") return "-";
    const num = Number(roi);
    if (isNaN(num)) return "-";
    return `${num.toFixed(3)}%`;
  };

  const quarterRows = [
    {
      quarter: "Q1",
      investment: unit.q1Investment,
      variance: unit.q1Investment > 0 && unit.q1Roi != null ? formatVariance(unit.q1Roi) : "-",
      returnGen: unit.q1Return,
      roi: unit.q1Investment > 0 ? unit.q1Roi : null,
    },
    {
      quarter: "Q2",
      investment: unit.q2Investment,
      variance: unit.q2Investment > 0 && unit.q2Roi != null ? formatVariance(unit.q2Roi) : "-",
      returnGen: unit.q2Return,
      roi: unit.q2Investment > 0 ? unit.q2Roi : null,
    },
    {
      quarter: "Q3",
      investment: unit.q3Investment,
      variance: unit.q3Investment > 0 && unit.q3Roi != null ? formatVariance(unit.q3Roi) : "-",
      returnGen: unit.q3Return,
      roi: unit.q3Investment > 0 ? unit.q3Roi : null,
    },
    {
      quarter: "Q4",
      investment: unit.q4Investment,
      variance: unit.q4Investment > 0 && unit.q4Roi != null ? formatVariance(unit.q4Roi) : "-",
      returnGen: unit.q4Return,
      roi: unit.q4Investment > 0 ? unit.q4Roi : null,
    },
  ];

  const topInvestors = unit.topInvestors || [];
  const hasMoreInvestors = topInvestors.length > 5;
  const visibleInvestors = showAllInvestors ? topInvestors : topInvestors.slice(0, 5);

  const othersAmount = !showAllInvestors && hasMoreInvestors
    ? topInvestors.slice(5).reduce((acc, inv) => acc + (inv.investedAmount || inv.amount || 0), 0)
    : 0;

  return (
    <Box
      sx={{
        background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: "10px",
        p: { xs: 2, md: 2.5 },
        height: "100%",
        maxHeight: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "5px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: t.border2 || "rgba(255,255,255,0.15)",
          borderRadius: "3px",
          "&:hover": {
            background: t.border3 || "rgba(255,255,255,0.25)",
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "17px", fontWeight: 700, color: t.text, mb: 0.5 }}>
            {unit.unitName}
          </Typography>
          <Box
            sx={{
              display: "inline-block",
              px: 1,
              py: 0.25,
              borderRadius: "4px",
              background: unit.unitType === "MAC" ? "rgba(0, 212, 146, 0.12)" : "rgba(56, 189, 248, 0.12)",
              border: `1px solid ${unit.unitType === "MAC" ? "rgba(0, 212, 146, 0.3)" : "rgba(56, 189, 248, 0.3)"}`,
              fontSize: "10px",
              fontWeight: 600,
              color: unit.unitType === "MAC" ? "#00D492" : "#38BDF8",
              textTransform: "uppercase",
            }}
          >
            {unit.unitType === "MAC" ? "MAC Unit" : "Group"}
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close detail panel"
          sx={{
            color: t.text3,
            "&:hover": { color: t.text, background: t.surface2 },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Mini KPI Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1.5,
          p: 1.5,
          background: t.surface2,
          borderRadius: "8px",
          border: `1px solid ${t.border2}`,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "9.5px", color: t.text3, fontWeight: 500, mb: 0.25, textTransform: "uppercase" }}>
            Total Investment
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: t.text }}>
            {formatCurrency(unit.totalInvestment)}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "9.5px", color: t.text3, fontWeight: 500, mb: 0.25, textTransform: "uppercase" }}>
            Total Return
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: unit.totalReturn >= 0 ? IL_COLORS.positive : IL_COLORS.negative,
            }}
          >
            {formatCurrency(unit.totalReturn)}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "9.5px", color: t.text3, fontWeight: 500, mb: 0.25, textTransform: "uppercase" }}>
            Overall ROI
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: unit.overallRoi >= 0 ? IL_COLORS.positive : IL_COLORS.negative,
            }}
          >
            {formatSignedPercent(unit.overallRoi)}
          </Typography>
        </Box>
      </Box>

      {/* Quarter-wise Details */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: t.text, mb: 1 }}>
          Quarter-wise Details
        </Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: `1px solid ${t.border}` }}>
                <Box component="th" sx={{ textAlign: "left", py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>QUARTER</Box>
                <Box component="th" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>INVESTMENT RECEIVED</Box>
                <Box component="th" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>VARIANCE</Box>
                <Box component="th" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>RETURN GENERATED</Box>
                <Box component="th" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>ROI</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {quarterRows.map((q) => {
                const roiColor = q.roi != null ? (q.roi >= 0 ? IL_COLORS.positive : IL_COLORS.negative) : t.text3;
                return (
                  <Box
                    component="tr"
                    key={q.quarter}
                    sx={{
                      borderBottom: `1px solid ${t.border}`,
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box component="td" sx={{ py: 0.75, px: 1, color: t.text, fontWeight: 500 }}>{q.quarter}</Box>
                    <Box component="td" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text2 }}>
                      {formatCurrency(q.investment)}
                    </Box>
                    <Box component="td" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text2 }}>
                      {q.variance}
                    </Box>
                    <Box component="td" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text2 }}>
                      {formatCurrency(q.returnGen)}
                    </Box>
                    <Box component="td" sx={{ textAlign: "right", py: 0.75, px: 1, color: roiColor, fontWeight: 600 }}>
                      {formatSignedPercent(q.roi)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: t.border, mb: 2 }} />

      {/* Top Investors into Unit */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: t.text }}>
            Top Investors into {unit.unitName}
          </Typography>
          <Typography sx={{ fontSize: "10.5px", color: t.text3 }}>
            Units that invested capital directly into {unit.shortName}
          </Typography>
        </Box>

        {topInvestors.length === 0 ? (
          <Typography sx={{ fontSize: "11px", color: t.text3, py: 2, textAlign: "center" }}>
            No investors recorded for this unit.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: `1px solid ${t.border}` }}>
                  <Box component="th" sx={{ textAlign: "left", py: 0.75, px: 1, color: t.text3, fontWeight: 600, width: "36px" }}>RANK</Box>
                  <Box component="th" sx={{ textAlign: "left", py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>INVESTOR UNIT</Box>
                  <Box component="th" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>AMOUNT</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {visibleInvestors.map((inv, idx) => (
                  <Box
                    component="tr"
                    key={idx}
                    sx={{
                      borderBottom: `1px solid ${t.border}`,
                      "&:hover": { background: t.surface2 },
                    }}
                  >
                    <Box component="td" sx={{ py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>
                      {inv.rank || idx + 1}
                    </Box>
                    <Box component="td" sx={{ py: 0.75, px: 1, color: t.text, fontWeight: 500 }}>
                      {inv.investorUnit}
                    </Box>
                    <Box component="td" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text2 }}>
                      {formatCurrency(inv.investedAmount || inv.amount || 0)}
                    </Box>
                  </Box>
                ))}

                {!showAllInvestors && hasMoreInvestors && (
                  <Box component="tr" sx={{ borderBottom: `1px solid ${t.border}` }}>
                    <Box component="td" sx={{ py: 0.75, px: 1, color: t.text3, fontWeight: 600 }}>
                      {visibleInvestors.length + 1}
                    </Box>
                    <Box component="td" sx={{ py: 0.75, px: 1, color: t.text2, fontStyle: "italic" }}>
                      Others ({topInvestors.length - 5} investors)
                    </Box>
                    <Box component="td" sx={{ textAlign: "right", py: 0.75, px: 1, color: t.text2 }}>
                      {formatCurrency(othersAmount)}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {hasMoreInvestors && (
              <Box sx={{ mt: 1.5, textAlign: "right" }}>
                <Typography
                  component="button"
                  onClick={() => setShowAllInvestors(!showAllInvestors)}
                  sx={{
                    background: "none",
                    border: "none",
                    color: "#00D492",
                    fontSize: "11px",
                    fontWeight: 600,
                    cursor: "pointer",
                    p: 0,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {showAllInvestors ? "Show top 5 only ↑" : "View all investors →"}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecipientDetailDrawer;

